// CommunicationApi.tsx

import {
  useQuery,
  useMutation,
  UseQueryOptions,
  QueryKey,
  useQueryClient,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

// Helper to replace path params like :messageId, :threadId, :id
function buildUrl(
  template: string,
  params: Record<string, string | number | undefined>,
) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? ":" + key),
  );
}

// Generic API response wrapper
interface ApiResponse<T> {
  isSuccess: boolean;
  message: string;
  data: T;
}

// Core message types based on the Postman examples
export type MessagePriority = "low" | "normal" | "high";
export type MessageType =
  | "personal"
  | "project_related"
  | "course_related"
  | string;

export interface Message {
  id: string;
  status?: string;
  is_read: boolean;
  metadata?: Record<string, any>;
  sender_id: string | null;
  recipient_id: string | null;
  group_id: string | null;
  subject: string | null;
  content: string | null;
  message_type?: MessageType;
  priority?: MessagePriority;
  attachments?: any[];
  parent_message_id?: string | null;
  thread_id: string;
  course_id?: string | null;
  project_id?: string | null;
  scheduled_for?: string | null;
  expires_at?: string | null;
  read_at?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserLite {
  id: string;
  name: string;
  email: string;
}

export interface GroupMemberUser {
  id: string;
  name: string;
  email: string;
}

export interface GroupMember {
  id: string;
  group_id: string;
  user_id: string;
  role: "admin" | "member" | string;
  joined_at: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
  user: GroupMemberUser;
}

export interface GroupLite {
  id: string;
  name: string;
  group_type: string;
  members: GroupMember[];
}

export interface InboxMessageView {
  id: string;
  thread_id: string;
  subject: string;
  content: string;
  is_read: boolean;
  priority: MessagePriority;
  group_id: string | null;
  sender: UserLite | null;
  recipient: UserLite | null;
  group: GroupLite | null;
  course: any | null;
  project: any | null;
  isGroup: boolean;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Request DTOs
export interface SendDirectMessageRequest {
  recipientId: string;
  subject?: string;
  content: string;
  messageType?: MessageType;
  priority?: MessagePriority;
  projectId?: string;
  courseId?: string;
  attachments?: any[];
  metadata?: Record<string, any>;
  scheduledFor?: string | null;
  expiresAt?: string | null;
}

export interface SendGroupMessageRequest {
  recipientId: string[];
  groupName?: string;
  subject?: string;
  content: string;
  messageType?: MessageType;
  priority?: MessagePriority;
  projectId?: string;
  courseId?: string;
  attachments?: any[];
  metadata?: Record<string, any>;
  scheduledFor?: string | null;
  expiresAt?: string | null;
}

export interface ReplyMessageRequest {
  subject?: string;
  content: string;
  attachments?: any[];
  metadata?: Record<string, any>;
}

export interface UpdateMessageRequest {
  subject?: string;
  content?: string;
  priority?: MessagePriority;
  attachments?: any[];
  metadata?: Record<string, any>;
}

export interface MarkReadBulkRequest {
  messageIds: string[];
}

// Response DTOs
export interface SendMessageResponse {
  message: Message;
  isGroup: boolean;
  groupId?: string;
}

export interface InboxResponse {
  messages: InboxMessageView[];
  pagination: Pagination;
}

export type ThreadResponse = Message[]; // thread is an array of messages

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface MarkReadBulkResponse {
  count: number;
}

// API functions

// Send direct one-to-one OR create group and send (same endpoint, payload differs)
export async function sendEnhancedMessage(
  payload: SendDirectMessageRequest | SendGroupMessageRequest,
): Promise<ApiResponse<SendMessageResponse>> {
  const url = getBITSEndApiMap("enhancedSendOneToOne");
  const { data } = await api.post<ApiResponse<SendMessageResponse>>(
    url,
    payload,
  );
  return data;
}

// Get inbox with optional pagination
export async function getInboxMessages(params?: {
  page?: number;
  limit?: number;
}): Promise<ApiResponse<InboxResponse>> {
  const baseUrl = getBITSEndApiMap("enhancedGetInbox");
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.limit) search.set("limit", String(params.limit));
  const url = search.toString() ? `${baseUrl}?${search.toString()}` : baseUrl;
  const { data } = await api.get<ApiResponse<InboxResponse>>(url);
  return data;
}

// Reply to a message by id
export async function replyToMessageById(
  id: string,
  payload: ReplyMessageRequest,
): Promise<ApiResponse<Message>> {
  const url = buildUrl(getBITSEndApiMap("enhancedReplyById"), { id });
  const { data } = await api.post<ApiResponse<Message>>(url, payload);
  return data;
}

// Get a full thread by threadId
export async function getThreadById(
  threadId: string,
): Promise<ApiResponse<ThreadResponse>> {
  const url = buildUrl(getBITSEndApiMap("enhancedGetThreadById"), { threadId });
  const { data } = await api.get<ApiResponse<ThreadResponse>>(url);
  return data;
}

// Mark a single message as read by messageId
export async function markMessageReadSingle(
  messageId: string,
): Promise<ApiResponse<Message>> {
  const url = buildUrl(getBITSEndApiMap("enhancedMarkReadSingle"), {
    messageId,
  });
  const { data } = await api.post<ApiResponse<Message>>(url);
  return data;
}

// Get unread count
export async function getUnreadCount(): Promise<
  ApiResponse<UnreadCountResponse>
> {
  const url = getBITSEndApiMap("enhancedGetUnreadCount");
  const { data } = await api.get<ApiResponse<UnreadCountResponse>>(url);
  return data;
}

// Mark multiple as read
export async function markMessagesReadBulk(
  payload: MarkReadBulkRequest,
): Promise<ApiResponse<MarkReadBulkResponse>> {
  const url = getBITSEndApiMap("enhancedMarkReadBulk");
  const { data } = await api.post<ApiResponse<MarkReadBulkResponse>>(
    url,
    payload,
  );
  return data;
}

// Get sent messages with pagination
export async function getSentMessages(params?: {
  page?: number;
  limit?: number;
}): Promise<ApiResponse<InboxResponse>> {
  const baseUrl = getBITSEndApiMap("enhancedGetSent");
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.limit) search.set("limit", String(params.limit));
  const url = search.toString() ? `${baseUrl}?${search.toString()}` : baseUrl;
  const { data } = await api.get<ApiResponse<InboxResponse>>(url);
  return data;
}

// Update a message by id
export async function updateMessageById(
  messageId: string,
  payload: UpdateMessageRequest,
): Promise<ApiResponse<Message>> {
  const url = buildUrl(getBITSEndApiMap("enhancedUpdateById"), { messageId });
  const { data } = await api.put<ApiResponse<Message>>(url, payload);
  return data;
}

// Delete a message by id
export async function deleteMessageById(
  messageId: string,
): Promise<ApiResponse<{ message: string }>> {
  const url = buildUrl(getBITSEndApiMap("enhancedDeleteById"), { messageId });
  const { data } = await api.delete<ApiResponse<{ message: string }>>(url);
  return data;
}

// React Query hooks

// Send message (direct or group) mutation
export function useSendEnhancedMessageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendDirectMessageRequest | SendGroupMessageRequest) =>
      sendEnhancedMessage(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "unreadCount"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "sent"] });
    },
  });
}

// Inbox query
export function useInboxQuery(
  params?: { page?: number; limit?: number },
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    refetchIntervalInBackground?: boolean;
    staleTime?: number;
  } = {},
) {
  const key: QueryKey = [
    "messages",
    "inbox",
    params?.page ?? 1,
    params?.limit ?? 20,
  ];
  return useQuery({
    queryKey: key,
    queryFn: () => getInboxMessages(params),
    enabled: options.enabled ?? true,
    staleTime: options.staleTime ?? 30_000,
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: options.refetchIntervalInBackground ?? false,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? true,
  } as UseQueryOptions<ApiResponse<InboxResponse>> as any);
}

// Reply mutation
export function useReplyMessageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ReplyMessageRequest;
    }) => replyToMessageById(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["messages", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "thread"] }); // generic thread cache key
      // If a specific threadId is known at caller, they should also invalidate that key.
    },
  });
}

// Thread query
export function useThreadQuery(
  threadId: string,
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    refetchIntervalInBackground?: boolean;
    staleTime?: number;
  } = {},
) {
  const key: QueryKey = ["messages", "thread", threadId];
  return useQuery<ApiResponse<ThreadResponse>>({
    queryKey: key,
    queryFn: () => getThreadById(threadId),
    enabled: (options.enabled ?? true) && !!threadId,
    staleTime: options.staleTime ?? 30_000,
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: options.refetchIntervalInBackground ?? false,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? true,
  });
}

// Mark single as read mutation
export function useMarkReadSingleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => markMessageReadSingle(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "unreadCount"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "thread"] });
    },
  });
}

// Unread count query
export function useUnreadCountQuery(enabled = true) {
  return useQuery({
    queryKey: ["messages", "unreadCount"] as QueryKey,
    queryFn: () => getUnreadCount(),
    enabled,
    staleTime: 10_000,
  } as UseQueryOptions<ApiResponse<UnreadCountResponse>> as any);
}

// Mark bulk read mutation
export function useMarkReadBulkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MarkReadBulkRequest) => markMessagesReadBulk(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "unreadCount"] });
    },
  });
}

// Sent messages query
export function useSentMessagesQuery(
  params?: { page?: number; limit?: number },
  enabled = true,
) {
  const key: QueryKey = [
    "messages",
    "sent",
    params?.page ?? 1,
    params?.limit ?? 20,
  ];
  return useQuery({
    queryKey: key,
    queryFn: () => getSentMessages(params),
    enabled,
    staleTime: 30_000,
  } as UseQueryOptions<ApiResponse<InboxResponse>> as any);
}

// Update message mutation
export function useUpdateMessageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      payload,
    }: {
      messageId: string;
      payload: UpdateMessageRequest;
    }) => updateMessageById(messageId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "sent"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "thread"] });
    },
  });
}

// Delete message mutation
export function useDeleteMessageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => deleteMessageById(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "sent"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "unreadCount"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "thread"] });
    },
  });
}

/* =========================
   Announcements - Types
   ========================= */

export type AnnouncementPriority = "low" | "normal" | "high";
export type AnnouncementStatus = "draft" | "published" | "archived" | string;
export type AnnouncementType = "general" | "project_update" | string;

export type TargetAudience =
  | "all"
  | "student_specific_course"
  | "student_specific_project"
  | "student_pending_submission"
  | string;

export interface AnnouncementEntity {
  id: string;
  title: string;
  content: string;
  announcement_type: AnnouncementType;
  priority: AnnouncementPriority;
  is_pinned: boolean;
  target_audience: TargetAudience[];
  attachments: any[];
  metadata?: Record<string, any>;
  created_by: string;
  status: AnnouncementStatus;
  updatedAt: string;
  createdAt: string;
  course_id: string | null;
  project_id: string | null;
  scheduled_for: string | null;
  expires_at: string | null;
  published_by: string | null;
  published_at: string | null;
  deletedAt: string | null;
}

export interface CreateAnnouncementRequest {
  title: string;
  content: string;
  targetAudience: TargetAudience[];
  courseId?: string | null;
  projectId?: string | null;
  announcementType?: AnnouncementType;
  priority?: AnnouncementPriority;
  attachments?: any[];
  metadata?: Record<string, any>;
  scheduledFor?: string | null;
  expiresAt?: string | null;
}

export interface TogglePinResponse extends AnnouncementEntity {}

export interface CourseProjectsResponse {
  projects: Array<{
    id: string;
    title: string;
    description: string | null;
    status: string;
    projectId: string;
  }>;
}

export interface UserListByRoleParams {
  page?: number;
  limit?: number;
  role?: string;
  search?: string;
}

export interface UserListByRoleResponse {
  users: Array<{
    id: string;
    name: string;
    email: string;
    role?: string;
  }>;
  pagination?: Pagination;
}

export interface GetAnnouncementsParams {
  page?: number;
  limit?: number;
  status?: AnnouncementStatus;
  courseId?: string;
  announcementType?: AnnouncementType;
  priority?: AnnouncementPriority;
  isPinned?: boolean;
  includeExpired?: boolean;
}

export interface AnnouncementsListItem {
  id: string;
  title: string;
  content: string;
  isPinned: boolean;
  project: null | {
    id: string;
    title: string;
    description: string | null;
    projectCode?: string;
    status?: string;
  };
  announcementType: AnnouncementType;
  course: null | {
    id: string;
    name: string;
    code?: string;
  };
  creator?: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  daysAgo?: number;
  isRead: boolean;
}

export interface GetAnnouncementsResponse {
  announcements: AnnouncementsListItem[];
  pagination?: Pagination;
}

/* =========================
   Announcements - API
   ========================= */

// GET /api/courses/projects?courseId=...
export async function getCourseProjects(params: {
  courseId: string;
}): Promise<ApiResponse<CourseProjectsResponse>> {
  const base = getBITSEndApiMap("getCourseProjects");
  const url = `${base}?${new URLSearchParams({ courseId: params.courseId }).toString()}`;
  const { data } = await api.get<ApiResponse<CourseProjectsResponse>>(url);
  return data;
}

// GET /api/users/list-by-role with optional filters
export async function getUserListByRole(
  params?: UserListByRoleParams,
): Promise<ApiResponse<UserListByRoleResponse>> {
  const base = getBITSEndApiMap("getUserListByRole");
  const search = new URLSearchParams();
  if (params?.page != null) search.set("page", String(params.page));
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.role) search.set("role", params.role);
  if (params?.search) search.set("search", params.search);
  const url = search.toString() ? `${base}?${search.toString()}` : base;
  const { data } = await api.get<ApiResponse<UserListByRoleResponse>>(url);
  return data;
}

// POST /api/announcements/enhanced
export async function createAnnouncement(
  payload: CreateAnnouncementRequest,
): Promise<ApiResponse<AnnouncementEntity>> {
  const url = getBITSEndApiMap("createAnnouncement");
  // Map camelCase to backend snake_case where needed
  const body: any = {
    title: payload.title,
    content: payload.content,
    targetAudience: payload.targetAudience,
  };
  if (payload.courseId !== undefined) body.courseId = payload.courseId;
  if (payload.projectId !== undefined) body.projectId = payload.projectId;
  if (payload.announcementType)
    body.announcementType = payload.announcementType;
  if (payload.priority) body.priority = payload.priority;
  if (payload.attachments) body.attachments = payload.attachments;
  if (payload.metadata) body.metadata = payload.metadata;
  if (payload.scheduledFor !== undefined)
    body.scheduledFor = payload.scheduledFor;
  if (payload.expiresAt !== undefined) body.expiresAt = payload.expiresAt;

  const { data } = await api.post<ApiResponse<AnnouncementEntity>>(url, body);
  return data;
}

// POST /api/announcements/enhanced/:id/toggle-pin
export async function toggleAnnouncementPin(
  id: string,
): Promise<ApiResponse<TogglePinResponse>> {
  const url = buildUrl(getBITSEndApiMap("togglePinStatus"), { id });
  const { data } = await api.post<ApiResponse<TogglePinResponse>>(url);
  return data;
}

// GET /api/announcements/enhanced with filters
export async function getAllAnnouncements(
  params?: GetAnnouncementsParams,
): Promise<ApiResponse<GetAnnouncementsResponse>> {
  const base = getBITSEndApiMap("getAllAnnouncements");
  const search = new URLSearchParams();
  if (params?.page != null) search.set("page", String(params.page));
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.status) search.set("status", params.status);
  if (params?.courseId) search.set("courseId", params.courseId);
  if (params?.announcementType)
    search.set("announcementType", params.announcementType);
  if (params?.priority) search.set("priority", params.priority);
  if (params?.isPinned != null) search.set("isPinned", String(params.isPinned));
  if (params?.includeExpired != null)
    search.set("includeExpired", String(params.includeExpired));
  const url = search.toString() ? `${base}?${search.toString()}` : base;
  const { data } = await api.get<ApiResponse<GetAnnouncementsResponse>>(url);
  return data;
}

/* =========================
   Announcements - Hooks
   ========================= */

// Course projects for a course
export function useCourseProjectsQuery(courseId: string, enabled = true) {
  const key: QueryKey = ["announcements", "courseProjects", courseId];
  return useQuery({
    queryKey: key,
    queryFn: () => getCourseProjects({ courseId }),
    enabled: enabled && !!courseId,
    staleTime: 30_000,
  } as UseQueryOptions<ApiResponse<CourseProjectsResponse>> as any);
}

// Users list by role
export function useUserListByRoleQuery(
  params?: UserListByRoleParams,
  enabled = true,
) {
  const key: QueryKey = [
    "announcements",
    "usersByRole",
    params?.role ?? "",
    params?.search ?? "",
    params?.page ?? 1,
    params?.limit ?? 20,
  ];
  return useQuery<ApiResponse<UserListByRoleResponse>>({
    queryKey: key,
    queryFn: () => getUserListByRole(params),
    enabled,
    staleTime: 30_000,
  });
}

// Create announcement
export function useCreateAnnouncementMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAnnouncementRequest) =>
      createAnnouncement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements", "list"] });
    },
  });
}

// Toggle pin
export function useToggleAnnouncementPinMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => toggleAnnouncementPin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements", "list"] });
    },
  });
}

// All announcements
export function useAnnouncementsListQuery(
  params?: GetAnnouncementsParams,
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    refetchIntervalInBackground?: boolean;
    staleTime?: number;
  } = {},
) {
  const key: QueryKey = [
    "announcements",
    "list",
    params?.page ?? 1,
    params?.limit ?? 20,
    params?.status ?? "",
    params?.courseId ?? "",
    params?.announcementType ?? "",
    params?.priority ?? "",
    params?.isPinned ?? "",
    params?.includeExpired ?? "",
  ];
  return useQuery({
    queryKey: key,
    queryFn: () => getAllAnnouncements(params),
    enabled: options.enabled ?? true,
    staleTime: options.staleTime ?? 30_000,
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: options.refetchIntervalInBackground ?? false,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? true,
  } as UseQueryOptions<ApiResponse<GetAnnouncementsResponse>> as any);
}

// ========== Notifications & Announcement Read ==========

export interface NotificationItem {
  id: string;
  title?: string | null;
  content?: string | null;
  isRead: boolean;
  createdAt: string;
  type?: string | null;
  metadata?: Record<string, any>;
}

export interface GetNotificationsResponse {
  notifications: NotificationItem[];
  pagination?: Pagination;
}

export interface NotificationCountResponse {
  count: number;
}

export type MarkNotificationsItem = {
  type: "message" | "announcement";
  id: string;
};

export interface MarkNotificationsReadRequest {
  items: MarkNotificationsItem[];
}

export interface MarkNotificationsReadResponse {
  messagesUpdated: number;
  announcementsUpdated: number;
  totalUpdated: number;
}

// API functions
export async function markAnnouncementRead(
  id: string,
): Promise<ApiResponse<AnnouncementEntity>> {
  const url = buildUrl(getBITSEndApiMap("markAnnouncementRead"), { id });
  const { data } = await api.post<ApiResponse<AnnouncementEntity>>(url);
  return data;
}

export async function getNotifications(params?: {
  page?: number;
  limit?: number;
}): Promise<ApiResponse<GetNotificationsResponse>> {
  const base = getBITSEndApiMap("getNotifications");
  const search = new URLSearchParams();
  if (params?.page != null) search.set("page", String(params.page));
  if (params?.limit != null) search.set("limit", String(params.limit));
  const url = search.toString() ? `${base}?${search.toString()}` : base;
  const { data } = await api.get<ApiResponse<GetNotificationsResponse>>(url);
  return data;
}

export async function getNotificationCount(): Promise<
  ApiResponse<NotificationCountResponse>
> {
  const url = getBITSEndApiMap("notificationsCounts");
  const { data } = await api.get<ApiResponse<NotificationCountResponse>>(url);
  return data;
}

export async function markNotificationsAllRead(
  payload: MarkNotificationsReadRequest,
): Promise<ApiResponse<MarkNotificationsReadResponse>> {
  const url = getBITSEndApiMap("notificationsMarkAllRead");
  const { data } = await api.post<ApiResponse<MarkNotificationsReadResponse>>(
    url,
    payload,
  );
  return data;
}

// Hooks
export function useMarkAnnouncementReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markAnnouncementRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements", "list"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "count"] });
    },
  });
}

export function useNotificationsQuery(
  params?: { page?: number; limit?: number },
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchIntervalInBackground?: boolean;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
  } = {},
) {
  const key: QueryKey = [
    "notifications",
    "list",
    params?.page ?? 1,
    params?.limit ?? 20,
  ];
  return useQuery({
    queryKey: key,
    queryFn: () => getNotifications(params),
    enabled: options.enabled ?? true,
    staleTime: options.staleTime ?? 30_000,
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: options.refetchIntervalInBackground ?? false,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? true,
  } as UseQueryOptions<ApiResponse<GetNotificationsResponse>> as any);
}

export function useNotificationCountQuery(
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchIntervalInBackground?: boolean;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
  } = {},
) {
  return useQuery({
    queryKey: ["notifications", "count"] as QueryKey,
    queryFn: () => getNotificationCount(),
    enabled: options.enabled ?? true,
    staleTime: options.staleTime ?? 10_000,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? true,
  } as UseQueryOptions<ApiResponse<NotificationCountResponse>> as any);
}

export function useMarkNotificationsAllReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MarkNotificationsReadRequest) =>
      markNotificationsAllRead(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "count"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "unreadCount"] });
      queryClient.invalidateQueries({ queryKey: ["messages", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["announcements", "list"] });
    },
  });
}
